import { ImportTable } from '../utils/importUtils';

/**
 * Configuration des tables pour l'importation
 * Définit la structure de chaque table avec ses colonnes et types
 */

// Helper function to get column descriptions
const getColumnDescription = (key: string, lang: 'fr' | 'en' = 'fr'): string => {
  const descriptions = {
    fr: {
      name: "Nom du lead",
      contactType: "Type de contact principal",
      contactValue: "Valeur du contact principal",
      sector: "Secteur d'activité",
      status: "Statut du lead",
      direction: "Direction (inbound/outbound)",
      notes: "Notes et commentaires",
      followupCount: "Nombre de relances",
      lastActionAt: "Dernière activité",
      nextActionAt: "Prochaine action"
    },
    en: {
      name: "Lead name",
      contactType: "Main contact type",
      contactValue: "Main contact value",
      sector: "Business sector",
      status: "Lead status",
      direction: "Direction (inbound/outbound)",
      notes: "Notes and comments",
      followupCount: "Number of follow-ups",
      lastActionAt: "Last activity",
      nextActionAt: "Next action"
    }
  };
  
  return descriptions[lang][key as keyof typeof descriptions.fr] || key;
};

export const LEADS_TABLE: ImportTable = {
  name: 'leads',
  displayName: 'Leads',
  primaryKey: 'id',
  columns: [
    {
      name: 'name',
      labelKey: 'leads.name',
      type: 'text',
      required: true,
      description: getColumnDescription('name')
    },
    {
      name: 'contact_type',
      labelKey: 'leads.contactType',
      type: 'text',
      required: false,
      description: getColumnDescription('contactType')
    },
    {
      name: 'contact_value',
      labelKey: 'leads.contactValue',
      type: 'text',
      required: false,
      description: getColumnDescription('contactValue')
    },
    {
      name: 'sector',
      labelKey: 'leads.sector',
      type: 'text',
      required: false,
      description: getColumnDescription('sector')
    },
    {
      name: 'status',
      labelKey: 'leads.status',
      type: 'text',
      required: true,
      description: getColumnDescription('status')
    },
    {
      name: 'direction',
      labelKey: 'leads.source',
      type: 'text',
      required: false,
      description: getColumnDescription('direction')
    },
    {
      name: 'notes',
      labelKey: 'leads.notes',
      type: 'text',
      required: false,
      description: getColumnDescription('notes')
    },
    {
      name: 'followup_count',
      labelKey: 'followups.count',
      type: 'number',
      required: false,
      description: getColumnDescription('followupCount')
    },
    {
      name: 'last_action_at',
      labelKey: 'leads.lastAction',
      type: 'date',
      required: false,
      description: getColumnDescription('lastActionAt')
    },
    {
      name: 'next_action_at',
      labelKey: 'leads.nextAction',
      type: 'date',
      required: false,
      description: getColumnDescription('nextActionAt')
    }
  ]
};

export const PROJECTS_TABLE: ImportTable = {
  name: 'projects',
  displayName: 'Projets',
  primaryKey: 'id',
  columns: [
    {
      name: 'name',
      labelKey: 'projects.name',
      type: 'text',
      required: true,
      description: 'Nom du projet'
    },
    {
      name: 'lead_id',
      labelKey: 'projects.lead',
      type: 'text',
      required: true,
      description: 'ID du lead'
    },
    {
      name: 'description',
      labelKey: 'projects.description',
      type: 'text',
      required: false,
      description: 'Description du projet'
    },
    {
      name: 'status',
      labelKey: 'projects.status',
      type: 'text',
      required: true,
      description: 'Statut du projet'
    },
    {
      name: 'budget',
      labelKey: 'projects.budget',
      type: 'number',
      required: false,
      description: 'Budget du projet'
    },
    {
      name: 'start_date',
      labelKey: 'projects.startDate',
      type: 'date',
      required: false,
      description: 'Date de début'
    },
    {
      name: 'end_date',
      labelKey: 'projects.endDate',
      type: 'date',
      required: false,
      description: 'Date de fin'
    },
    {
      name: 'notes',
      labelKey: 'common.notes',
      type: 'text',
      required: false,
      description: 'Notes du projet'
    }
  ]
};

export const PAYMENTS_TABLE: ImportTable = {
  name: 'payments',
  displayName: 'Paiements',
  primaryKey: 'id',
  columns: [
    {
      name: 'project_id',
      labelKey: 'payments.project',
      type: 'text',
      required: true,
      description: 'ID du projet'
    },
    {
      name: 'amount',
      labelKey: 'payments.amount',
      type: 'number',
      required: true,
      description: 'Montant du paiement'
    },
    {
      name: 'status',
      labelKey: 'payments.status',
      type: 'text',
      required: true,
      description: 'Statut du paiement'
    },
    {
      name: 'due_date',
      labelKey: 'payments.dueDate',
      type: 'date',
      required: true,
      description: 'Date d\'échéance'
    },
    {
      name: 'paid_date',
      labelKey: 'payments.paymentDate',
      type: 'date',
      required: false,
      description: 'Date de paiement'
    },
    {
      name: 'invoice_number',
      labelKey: 'payments.invoiceNumber',
      type: 'text',
      required: false,
      description: 'Numéro de facture'
    },
    {
      name: 'notes',
      labelKey: 'common.notes',
      type: 'text',
      required: false,
      description: 'Notes du paiement'
    }
  ]
};

export const ORGANIZATION_TABLE: ImportTable = {
  name: 'organization',
  displayName: 'Organisation',
  primaryKey: 'id',
  columns: [
    {
      name: 'title',
      labelKey: 'organization.export.title',
      type: 'text',
      required: true,
      description: 'Titre de l\'élément'
    },
    {
      name: 'content',
      labelKey: 'organization.export.content',
      type: 'text',
      required: false,
      description: 'Contenu'
    },
    {
      name: 'category',
      labelKey: 'organization.export.category',
      type: 'text',
      required: false,
      description: 'Catégorie'
    },
    {
      name: 'type',
      labelKey: 'organization.export.type',
      type: 'text',
      required: true,
      description: 'Type (note, document, rappel, activité)'
    },
    {
      name: 'file_type',
      labelKey: 'organization.export.fileType',
      type: 'text',
      required: false,
      description: 'Type de fichier'
    },
    {
      name: 'file_size',
      labelKey: 'organization.export.size',
      type: 'text',
      required: false,
      description: 'Taille du fichier'
    },
    {
      name: 'due_date',
      labelKey: 'common.dueDate',
      type: 'date',
      required: false,
      description: 'Date d\'échéance'
    },
    {
      name: 'completed',
      labelKey: 'common.completed',
      type: 'boolean',
      required: false,
      description: 'Terminé'
    }
  ]
};

// Export all tables
export const TABLES = {
  leads: LEADS_TABLE,
  projects: PROJECTS_TABLE,
  payments: PAYMENTS_TABLE,
  organization: ORGANIZATION_TABLE
} as const;

export type TableName = keyof typeof TABLES;
